import React, { useState, useEffect, useRef } from "react";
import {
  Table, TableContainer, TableHead, TableBody, TableRow, TableCell,
  Paper, TextField, IconButton, InputAdornment, TablePagination,
  Chip, Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Tooltip, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Fab from "@mui/material/Fab";
import LinearProgress from "@mui/material/LinearProgress";
import {
  getSpecialistsAction,
  cleanSpecialistAction,
  getSpecialistAction,
  setOffset,
  setQuery,
} from "../../redux/actions/specialist";
import strings from "../../i18n/language";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyIcon from "@mui/icons-material/Key";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import SpecialistDelete from "../../dialog/SpecialistDelete";
import SpecialistAvailabilities from "../../dialog/SpecialistAvailabilities";
import { resetSpecialistPasswordAction } from "../../redux/actions/specialist";
import { toast } from "react-toastify";

const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const LOWER = "abcdefghjkmnpqrstuvwxyz";
const DIGITS = "23456789";
const SPECIAL = "!@#$%&*?";

const generatePassword = () => {
  const all = UPPER + LOWER + DIGITS + SPECIAL;
  const mandatory = [
    UPPER[Math.floor(Math.random() * UPPER.length)],
    LOWER[Math.floor(Math.random() * LOWER.length)],
    DIGITS[Math.floor(Math.random() * DIGITS.length)],
    SPECIAL[Math.floor(Math.random() * SPECIAL.length)],
  ];
  const rest = Array.from({ length: 8 }, () => all[Math.floor(Math.random() * all.length)]);
  return [...mandatory, ...rest].sort(() => Math.random() - 0.5).join("");
};

const passwordRules = (pwd) => ({
  length: pwd.length >= 8,
  upper: /[A-Z]/.test(pwd),
  lower: /[a-z]/.test(pwd),
  digit: /[0-9]/.test(pwd),
  special: /[!@#$%&*?]/.test(pwd),
});

const passwordValid = (pwd) => Object.values(passwordRules(pwd)).every(Boolean);

const SpecialistList = () => {
  const specialists = useSelector((state) => state.specialist.specialists);
  const user = useSelector((state) => state.user.user);
  const [deleteId, setDeleteId] = useState(null);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openAvailabilities, setOpenAvailabilities] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // { id, mail }
  const [newPassword, setNewPassword] = useState("");
  const dispatch = useDispatch();
  const specialistsRef = useRef([]);
  const navigate = useNavigate();
  const total = useSelector((state) => state.specialist.total);
  const limit = useSelector((state) => state.specialist.limit);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);

  const isLoadingGetSpecialists = useSelector(
    (state) => state.specialist.isLoadingGetSpecialists
  );

  useEffect(() => {
    dispatch(getSpecialistsAction());
  }, [dispatch]);

  useEffect(() => {
    if (specialists) specialistsRef.current = specialists;
  }, [specialists]);

  useEffect(() => {
    setSearching(true);
    setPage(0);
    const timeoutId = setTimeout(() => {
      setSearching(false);
      dispatch(setQuery(searchQuery));
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, dispatch]);

  return (
    <>
      <Paper sx={{ p: 2, width: "80%" }}>
        {searching && isLoadingGetSpecialists && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              width: "100%",
            }}
          />
        )}
        <TextField
          fullWidth
          variant="outlined"
          placeholder={strings.LABEL_SEARCH}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: { borderRadius: 25 },
          }}
          sx={{ mb: 2 }}
        />
        {specialists && (
          <TableContainer>
            <Table aria-label="Specialists table">
              <TableHead>
                <TableRow>
                  <TableCell>{strings.LABEL_SPECIALTY}</TableCell>
                  <TableCell>{strings.LABEL_NAME}</TableCell>
                  <TableCell>{strings.LABEL_PHONE}</TableCell>
                  <TableCell>{strings.LABEL_MAIL}</TableCell>
                  <TableCell>{strings.LABEL_ACTION}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {specialists.map((specialist) => (
                  <TableRow key={specialist.id}>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(specialist.specialty || "")
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean)
                          .map((s) => (
                            <Chip key={s} label={s} size="small" />
                          ))}
                        {specialist.medicinaOcupacional && (
                          <Chip label="Ocupacional" size="small" color="primary" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{specialist.displayName}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {specialist.phone}
                    </TableCell>
                    <TableCell>{specialist.email}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <IconButton
                        aria-label="availabilities"
                        onClick={() => {
                          dispatch(getSpecialistAction(specialist.id));
                          setOpenAvailabilities(true);
                        }}
                      >
                        <CalendarMonthIcon />
                      </IconButton>
                      {user.type === "admin" && (
                        <Tooltip title={specialist.email ? "Réinitialiser contraseña" : "Se necesita un correo válido para generar una contraseña"}>
                          <span>
                            <IconButton
                              disabled={!specialist.email}
                              onClick={() => {
                                const pwd = generatePassword();
                                setNewPassword(pwd);
                                setResetTarget({ id: specialist.id, mail: specialist.email });
                              }}
                            >
                              <KeyIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {user.type !== "viewer" && (
                        <>
                          <IconButton
                            aria-label="delete"
                            onClick={() => {
                              setDeleteId(specialist.id);
                              setOpenModalDelete(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                          <IconButton
                            aria-label="edit"
                            onClick={() => {
                              dispatch(getSpecialistAction(specialist.id));
                              navigate("/specialists/edit");
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[50, 100]}
              count={total}
              rowsPerPage={limit || 50}
              page={page}
              onPageChange={(event, newPage) => {
                dispatch(setOffset(limit * newPage));
                setPage(newPage);
              }}
              component="div"
            />
          </TableContainer>
        )}
      </Paper>
      {user.type !== "viewer" && (
        <Fab
          style={{ alignSelf: "flex-end", marginLeft: 10 }}
          color="primary"
          aria-label="add"
          onClick={() => {
            dispatch(cleanSpecialistAction());
            navigate("/specialists/edit");
          }}
        >
          <AddIcon />
        </Fab>
      )}
      <SpecialistDelete
        open={openModalDelete}
        setOpen={setOpenModalDelete}
        specialistDeleteId={deleteId}
      />
      <SpecialistAvailabilities
        open={openAvailabilities}
        setOpen={setOpenAvailabilities}
      />

      {/* ── Reset password dialog ── */}
      <Dialog open={!!resetTarget} onClose={() => setResetTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Réinitialiser contraseña</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            sx={{ mt: 1 }}
            label="Nueva contraseña"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={newPassword.length > 0 && !passwordValid(newPassword)}
            helperText={resetTarget?.mail ? `Se aplicará a la cuenta: ${resetTarget.mail}` : ""}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Regenerar">
                    <IconButton onClick={() => setNewPassword(generatePassword())}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copiar">
                    <IconButton onClick={() => { navigator.clipboard.writeText(newPassword); toast.info("Contraseña copiada"); }}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          {newPassword.length > 0 && !passwordValid(newPassword) && (() => {
            const rules = passwordRules(newPassword);
            return (
              <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {[
                  { ok: rules.length, label: "Mín. 8 caracteres" },
                  { ok: rules.upper, label: "Mayúscula" },
                  { ok: rules.lower, label: "Minúscula" },
                  { ok: rules.digit, label: "Número" },
                  { ok: rules.special, label: "Carácter especial (!@#$%&*?)" },
                ].map(({ ok, label }) => (
                  <Typography key={label} variant="caption" sx={{ color: ok ? "success.main" : "error.main" }}>
                    {ok ? "✓" : "✗"} {label}
                  </Typography>
                ))}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!passwordValid(newPassword)}
            onClick={() => {
              dispatch(resetSpecialistPasswordAction(resetTarget.id, newPassword));
              toast.success("Contraseña actualizada");
              setResetTarget(null);
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SpecialistList;
